/**
 * DV-BILL-001: Billing Commits Viewer
 * Admin-only viewer for billing_commits and related api_calls for selected execution
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, DollarSign, Clock, Database, AlertCircle, Loader2, RefreshCw, Wifi, WifiOff, TrendingUp, TrendingDown, Minus, ExternalLink, FileText } from 'lucide-react';
import { API_ENDPOINTS, buildApiUrl, getApiHeadersGet } from '@/lib/api-base';
import { formatCurrency, formatTimestamp } from '@/lib/format-utils';
import { demoQueryKeys, demoCacheInvalidation, demoPollingIntervals } from '@/lib/demo-query-client';
import { useDemoWebSocket, type DemoWebSocketEvent } from '@/lib/demo-websocket-client';
import { fetchWithErrorHandling, getDemoError, logDemoError } from '@/lib/demo-error-handler';
// VirtualizedList import removed - using threshold-based approach for now
import type { LogEntry } from './LogsPanel';

export interface BillingCommitsViewerProps {
  executionId: string;
  onExecutionChange?: (executionId: string) => void;
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Performance constants
const MAX_API_CALLS_PER_FETCH = 500; // Prevent extremely large IN queries
const BILLING_DEBOUNCE_MS = 300; // Debounce execution ID changes
const DEFAULT_PAGE_SIZE = 20; // Default commits per page
const MAX_PAGE_SIZE = 100; // Maximum commits per page

// Virtualization constants
const VIRTUALIZATION_THRESHOLD = 50; // Virtualize when > 50 items

// Enhanced data models with pagination metadata
interface BillingCommit {
  id: string;
  execution_id: string;
  committed_at: string;
  committed_delta_usd: number;
  pipeline_total_usd: number;
  attempt: number;
  committed_call_ids: string[];
  created_at: string;
  updated_at: string;
  // Enhanced fields for cost analysis
  previous_pipeline_total_usd?: number;
  call_count?: number;
  provider_breakdown?: Record<string, number>;
}

interface BillingCommitsResponse {
  data: BillingCommit[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  metadata: {
    executionId: string;
    totalCommittedCost: number;
    finalPipelineTotal: number;
    costDelta: number;
    requestId: string;
    generatedAt: string;
  };
}

interface ApiCall {
  id: string;
  execution_id: string;
  provider: string;
  model: string;
  status: string;
  cost_usd: number;
  input_tokens?: number;
  output_tokens?: number;
  created_at: string;
  completed_at?: string;
  // Enhanced fields for analysis
  duration_ms?: number;
  error_message?: string;
  retry_count?: number;
  stage_name?: string;
  operation_type?: string;
}

interface ApiCallsResponse {
  data: ApiCall[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  metadata: {
    commitId: string;
    totalCost: number;
    providerBreakdown: Record<string, { count: number; cost: number; }>;
    statusBreakdown: Record<string, number>;
    requestId: string;
  };
}

// Helper function to determine if virtualization should be used
const shouldVirtualize = (itemCount: number): boolean => {
  return itemCount > VIRTUALIZATION_THRESHOLD;
};

// Performance note logged when virtualization kicks in
const logVirtualizationUsage = (type: string, itemCount: number) => {
  console.log(`[Performance] Using virtualization for ${type}: ${itemCount} items (threshold: ${VIRTUALIZATION_THRESHOLD})`);
};

export function BillingCommitsViewer({
  executionId,
  onExecutionChange,
  onLog,
  className = '',
  'data-testid': testId = 'billing-commits-viewer'
}: BillingCommitsViewerProps) {
  const [expandedCommits, setExpandedCommits] = useState<Set<string>>(new Set());
  const [localExecutionId, setLocalExecutionId] = useState(executionId);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // React Query client for manual operations
  const queryClient = useQueryClient();

  // Request management for local execution ID changes
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // WebSocket client for cache invalidation hints
  const { isConnected: wsConnected } = useDemoWebSocket(queryClient, {
    executionId,
    autoConnect: true,
    onEvent: (event: DemoWebSocketEvent) => {
      addLog('websocket', `Cache hint received: ${event.type}`, 'debug', {
        event: event.type,
        executionId: event.executionId,
        stageName: event.stageName,
        timestamp: event.timestamp
      });
    },
    onConnectionChange: (connected: boolean) => {
      addLog('websocket', `WebSocket ${connected ? 'connected' : 'disconnected'}`, connected ? 'info' : 'warn', {
        connected,
        executionId
      });
    },
    onError: (error: Error) => {
      addLog('websocket', `WebSocket error: ${error.message}`, 'error', {
        error: error.message,
        executionId
      });
    }
  });

  // Enhanced logging utility
  const addLog = useCallback((source: string, message: string, level: LogEntry['level'] = 'info', extraMeta?: any) => {
    const entry: LogEntry = {
      id: Date.now().toString(),
      ts: new Date().toISOString(),
      level,
      text: `[${source.toUpperCase()}] ${message}`,
      meta: {
        source,
        executionId,
        ...extraMeta
      }
    };
    onLog?.(entry);
  }, [onLog, executionId]);

  // Build paginated commits endpoint URL
  const buildCommitsUrl = useCallback((page: number, limit: number) => {
    const params = new URLSearchParams({
      execution_id: executionId,
      page: page.toString(),
      limit: limit.toString()
    });
    return `/api/demo/billing/commits?${params}`;
  }, [executionId]);

  // Fetch billing commits using React Query with pagination
  const {
    data: commitsResponse,
    isLoading,
    error: commitsError,
  } = useQuery({
    queryKey: [...demoQueryKeys.billingCommits(executionId), currentPage, pageSize],
    queryFn: async () => {
      const startTime = performance.now();
      addLog('billing', `Fetching billing commits for execution ${executionId}`, 'info', {
        action: 'fetch_commits_start'
      });

      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 15000); // 15 second timeout

      let response: Response;
      let requestId: string;

      try {
        response = await fetchWithErrorHandling(buildApiUrl(buildCommitsUrl(currentPage, pageSize)), {
          method: 'GET',
          credentials: 'include', // Cookie-based auth
          headers: getApiHeadersGet(), // Preflight-safe headers
          signal: abortController.signal
        });
      } catch (error) {
        clearTimeout(timeoutId);

        // Handle demo error with structured logging
        const demoError = getDemoError(error);
        if (demoError) {
          const errorLogEntry = logDemoError(demoError);
          addLog('billing', errorLogEntry.text, 'error', {
            ...errorLogEntry.meta,
            action: 'fetch_commits_error',
            duration: Math.round(performance.now() - startTime)
          });

          // Add user-friendly notice for permission errors
          if (demoError.type === 'permission_denied') {
            addLog('billing', '🔒 Admin access required for billing data', 'warn', {
              remediation: demoError.remediation,
              correlationId: demoError.correlationId
            });
          }
        } else {
          // Fallback for non-demo errors
          addLog('billing', `❌ Failed to fetch billing commits: ${error}`, 'error', {
            action: 'fetch_commits_error',
            error: error instanceof Error ? error.message : String(error),
            duration: Math.round(performance.now() - startTime)
          });
        }

        throw error;
      }

      clearTimeout(timeoutId);
      requestId = response.headers.get('X-Request-Id') || 'unknown';

      const responseData = await response.json();

      // Enhanced response structure with pagination metadata
      const commitsResponse: BillingCommitsResponse = {
        data: responseData?.data || [],
        pagination: responseData?.pagination || {
          page: currentPage,
          limit: pageSize,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false
        },
        metadata: {
          executionId,
          totalCommittedCost: responseData?.metadata?.totalCommittedCost || 0,
          finalPipelineTotal: responseData?.metadata?.finalPipelineTotal || 0,
          costDelta: responseData?.metadata?.costDelta || 0,
          requestId,
          generatedAt: new Date().toISOString()
        }
      };

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      addLog('billing', `✅ Fetched ${commitsResponse.data.length} billing commits (page ${currentPage}/${commitsResponse.pagination.totalPages}) in ${duration}ms`, 'info', {
        action: 'fetch_commits_success',
        commitCount: commitsResponse.data.length,
        page: currentPage,
        totalPages: commitsResponse.pagination.totalPages,
        totalCommits: commitsResponse.pagination.total,
        duration,
        requestId
      });

      return commitsResponse;
    },
    enabled: !!executionId,
    staleTime: 5000, // 5 seconds (billing updates during execution)
    refetchInterval: demoPollingIntervals.billingCommits,
    // Keep previous data when paginating
    placeholderData: (previousData: any) => previousData,
  });

  const commits = commitsResponse?.data || [];
  const pagination = commitsResponse?.pagination;
  const metadata = commitsResponse?.metadata;

  // Fetch API calls for each commit using React Query
  const commitIds = commits.map((c: BillingCommit) => c.id);

  // Create queries for each commit's API calls
  const apiCallsQueries = useQuery({
    queryKey: demoQueryKeys.billingApiCallsAggregate(executionId, commitIds),
    queryFn: async () => {
      if (commitIds.length === 0) {
        addLog('billing', 'No commits available for API calls fetch', 'debug');
        return {};
      }

      const startTime = performance.now();
      const allCallIds = new Set<string>();
      commits.forEach((commit: BillingCommit) => {
        commit.committed_call_ids.forEach((id: string) => allCallIds.add(id));
      });

      addLog('billing', `Fetching API calls for ${commitIds.length} commits (${allCallIds.size} total calls)`, 'info', {
        action: 'fetch_api_calls_start',
        commitCount: commitIds.length,
        callIdCount: allCallIds.size
      });

      // Performance cap: limit API calls per fetch to prevent huge queries
      if (allCallIds.size > MAX_API_CALLS_PER_FETCH) {
        addLog('billing', `⚠️ Large API calls query: ${allCallIds.size} calls (capped at ${MAX_API_CALLS_PER_FETCH})`, 'warn', {
          totalCalls: allCallIds.size,
          cappedAt: MAX_API_CALLS_PER_FETCH
        });
      }

      const callsMap: Record<string, ApiCall> = {};

      // Fetch calls for each commit individually (since backend expects commit_id)
      for (const commit of commits) {
        try {
          const callsAbortController = new AbortController();
          const callsTimeoutId = setTimeout(() => callsAbortController.abort(), 10000); // 10 second timeout per call

          let callsResponse: Response;
          let callsRequestId: string;

          try {
            callsResponse = await fetchWithErrorHandling(buildApiUrl(API_ENDPOINTS.demo.billingCalls(commit.id)), {
              method: 'GET',
              credentials: 'include', // Cookie-based auth
              headers: getApiHeadersGet(), // Preflight-safe headers
              signal: callsAbortController.signal
            });

            clearTimeout(callsTimeoutId);
            callsRequestId = callsResponse.headers.get('X-Request-Id') || 'unknown';

            const callsData = await callsResponse.json();
            const callsResponse_typed: ApiCallsResponse = {
              data: callsData?.data || [],
              pagination: callsData?.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
              metadata: {
                commitId: commit.id,
                totalCost: callsData?.metadata?.totalCost || 0,
                providerBreakdown: callsData?.metadata?.providerBreakdown || {},
                statusBreakdown: callsData?.metadata?.statusBreakdown || {},
                requestId: callsRequestId
              }
            };
            callsResponse_typed.data.forEach((call: ApiCall) => {
              callsMap[call.id] = call;
            });
          } catch (callError) {
            clearTimeout(callsTimeoutId);

            // Handle demo error for individual calls
            const demoError = getDemoError(callError);
            if (demoError) {
              const errorLogEntry = logDemoError(demoError);
              addLog('billing', `⚠️ ${errorLogEntry.text} for commit ${commit.id}`, 'warn', {
                ...errorLogEntry.meta,
                commitId: commit.id
              });
            } else {
              // Fallback logging for non-demo errors
              addLog('billing', `⚠️ Failed to fetch calls for commit ${commit.id}`, 'warn', {
                commitId: commit.id,
                error: callError instanceof Error ? callError.message : String(callError)
              });
            }
          }
        } catch (callError) {
          // Additional catch for any non-fetch errors (e.g., JSON parsing)
          addLog('billing', `⚠️ Failed to process calls for commit ${commit.id}`, 'warn', {
            commitId: commit.id,
            error: callError instanceof Error ? callError.message : String(callError)
          });
        }
      }

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);

      const fetchedCount = Object.keys(callsMap).length;
      const requestedCount = allCallIds.size;
      const missingCount = requestedCount - fetchedCount;

      addLog('billing', `✅ Fetched ${fetchedCount} API calls in ${duration}ms`, 'info', {
        action: 'fetch_api_calls_success',
        fetchedCount,
        requestedCount,
        missingCount,
        duration
      });

      // Log missing API calls for debugging
      if (missingCount > 0) {
        const missingIds = Array.from(allCallIds).filter(id => !callsMap[id]);
        const exampleMissingId = missingIds[0];
        addLog('billing', `⚠️ ${missingCount} API calls not found in database`, 'warn', {
          action: 'api_calls_missing',
          missingCount,
          exampleMissingId,
          totalRequested: requestedCount
        });
      }

      return callsMap;
    },
    enabled: !!executionId && commits.length > 0,
    staleTime: 10000, // 10 seconds (API calls don't change often after commit)
    refetchInterval: false, // No automatic polling for API calls
  });

  const apiCalls = apiCallsQueries.data || {};

  // Derive loading state and errors from React Query
  const loading = isLoading || apiCallsQueries.isLoading;

  // Extract demo errors for better error handling
  const commitsDemoError = commitsError ? getDemoError(commitsError) : null;
  const apiCallsDemoError = apiCallsQueries.error ? getDemoError(apiCallsQueries.error) : null;

  const primaryError = commitsDemoError || apiCallsDemoError;
  const error = primaryError?.userMessage || commitsError?.message || apiCallsQueries.error?.message || null;
  const permissionDenied = primaryError?.type === 'permission_denied';

  // Toggle commit expansion
  const toggleCommitExpansion = useCallback((commitId: string) => {
    setExpandedCommits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commitId)) {
        newSet.delete(commitId);
        addLog('billing', `Collapsed commit details: ${commitId}`, 'debug', {
          action: 'collapse_commit',
          commitId
        });
      } else {
        newSet.add(commitId);
        addLog('billing', `Expanded commit details: ${commitId}`, 'debug', {
          action: 'expand_commit',
          commitId
        });
      }
      return newSet;
    });
  }, [addLog]);

  // Pagination handlers
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    addLog('billing', `Navigating to page ${newPage}`, 'debug', {
      action: 'page_change',
      page: newPage,
      totalPages: pagination?.totalPages || 0
    });
  }, [addLog, pagination?.totalPages]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(Math.min(newPageSize, MAX_PAGE_SIZE));
    setCurrentPage(1); // Reset to first page when changing page size
    addLog('billing', `Changing page size to ${newPageSize}`, 'debug', {
      action: 'page_size_change',
      pageSize: newPageSize
    });
  }, [addLog]);

  // Enhanced cost delta calculation with basis information
  const getCommitCostDelta = useCallback((commit: BillingCommit, index: number): {
    delta: number;
    basis: string;
    isRobust: boolean;
    previousCommit?: BillingCommit;
  } => {
    if (index === commits.length - 1) {
      // First commit (chronologically) - compare to 0
      return {
        delta: commit.committed_delta_usd,
        basis: 'vs. pipeline start (0)',
        isRobust: true
      };
    }

    // Compare to previous commit
    const previousCommit = commits[index + 1]; // Next in array is previous chronologically

    if (!previousCommit) {
      // Guard against missing previous commit
      return {
        delta: 0,
        basis: 'no previous commit',
        isRobust: false
      };
    }

    // Check for currency consistency (basic guard)
    const currentTotal = commit.pipeline_total_usd ?? 0;
    const previousTotal = previousCommit.pipeline_total_usd ?? 0;

    const calculatedDelta = currentTotal - previousTotal;

    return {
      delta: calculatedDelta,
      basis: `vs. Attempt #${previousCommit.attempt}`,
      isRobust: true,
      previousCommit
    };
  }, [commits]);

  // Get cost delta visualization components
  const getCostDeltaVisualization = useCallback((delta: number) => {
    const absValue = Math.abs(delta);

    if (delta > 0) {
      return {
        icon: TrendingUp,
        color: 'var(--fg-warning)',
        bgColor: 'var(--bg-warning)',
        label: `+${formatCurrency(absValue)} increase`,
        direction: 'increase' as const
      };
    } else if (delta < 0) {
      return {
        icon: TrendingDown,
        color: 'var(--fg-success)',
        bgColor: 'var(--bg-success)',
        label: `${formatCurrency(absValue)} decrease`,
        direction: 'decrease' as const
      };
    } else {
      return {
        icon: Minus,
        color: 'var(--fg-muted)',
        bgColor: 'var(--bg-surface)',
        label: 'No change',
        direction: 'neutral' as const
      };
    }
  }, []);

  // Generate commit deep link URL
  const getCommitDeepLink = useCallback((commit: BillingCommit) => {
    const params = new URLSearchParams({
      execution_id: commit.execution_id,
      commit_id: commit.id,
      page: currentPage.toString()
    });
    return `${window.location.origin}${window.location.pathname}?${params}`;
  }, [currentPage]);

  // Copy commit link to clipboard
  const copyCommitLink = useCallback(async (commit: BillingCommit) => {
    try {
      const url = getCommitDeepLink(commit);
      await navigator.clipboard.writeText(url);
      addLog('billing', `Copied commit link to clipboard: ${commit.id}`, 'info', {
        action: 'copy_commit_link',
        commitId: commit.id,
        url
      });
      return true;
    } catch (error) {
      addLog('billing', `Failed to copy commit link: ${error}`, 'error', {
        action: 'copy_commit_link_failed',
        commitId: commit.id,
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, [addLog, getCommitDeepLink]);

  // Generate comprehensive billing status report
  const generateBillingStatusReport = useCallback((): string => {
    const timestamp = new Date().toISOString();
    const reportId = `billing-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;

    // Compute statistics
    const totalCommits = pagination?.total || commits.length;
    const totalApiCalls = commits.reduce((sum, c) => sum + c.committed_call_ids.length, 0);
    const totalCost = metadata?.totalCommittedCost || commits.reduce((sum, c) => sum + c.committed_delta_usd, 0);
    const finalPipelineTotal = metadata?.finalPipelineTotal || (commits.length > 0 ? commits[0].pipeline_total_usd : 0);

    // Provider breakdown from loaded API calls
    const providerStats = Object.values(apiCalls).reduce((acc, call) => {
      const key = `${call.provider}/${call.model}`;
      if (!acc[key]) {
        acc[key] = { count: 0, cost: 0, statuses: {} };
      }
      acc[key].count += 1;
      acc[key].cost += call.cost_usd;
      acc[key].statuses[call.status] = (acc[key].statuses[call.status] || 0) + 1;
      return acc;
    }, {} as Record<string, { count: number; cost: number; statuses: Record<string, number> }>);

    // Error analysis
    const hasErrors = !!primaryError;
    const errorType = primaryError?.type || 'none';
    const isRetryable = primaryError?.retryable || false;

    // Performance metrics
    const loadingState = {
      isLoading: loading,
      commitsFetched: commits.length,
      apiCallsFetched: Object.keys(apiCalls).length,
      wsConnected
    };

    let report = `📊 Billing Commits Status Report - ${timestamp}\n\n`;

    // Executive Summary
    report += `📝 Executive Summary\n`;
    report += `Execution ID: ${executionId}\n`;
    report += `Total Commits: ${totalCommits}${pagination ? ` (Page ${pagination.page}/${pagination.totalPages})` : ''}\n`;
    report += `Total API Calls: ${totalApiCalls}${pagination ? ' (page)' : ''}\n`;
    report += `Total Cost: ${formatCurrency(totalCost)}\n`;
    report += `Final Pipeline Total: ${formatCurrency(finalPipelineTotal)}\n`;
    report += `Status: ${hasErrors ? `⚠️ ${errorType.toUpperCase()}` : '✅ HEALTHY'}\n\n`;

    // Pagination Info
    if (pagination) {
      report += `📊 Pagination Status\n`;
      report += `Current Page: ${pagination.page} of ${pagination.totalPages}\n`;
      report += `Page Size: ${pageSize}\n`;
      report += `Total Records: ${pagination.total}\n`;
      report += `Has Next: ${pagination.hasNextPage ? 'Yes' : 'No'}\n`;
      report += `Has Previous: ${pagination.hasPreviousPage ? 'Yes' : 'No'}\n\n`;
    }

    // Provider Breakdown
    if (Object.keys(providerStats).length > 0) {
      report += `🤖 Provider Breakdown\n`;
      Object.entries(providerStats)
        .sort(([,a], [,b]) => b.cost - a.cost)
        .forEach(([provider, stats]) => {
          const successRate = ((stats.statuses.completed || 0) / stats.count * 100).toFixed(1);
          report += `${provider}: ${stats.count} calls, ${formatCurrency(stats.cost)}, ${successRate}% success\n`;
        });
      report += '\n';
    }

    // Recent Commits Analysis
    if (commits.length > 0) {
      report += `🔄 Recent Commits (${commits.length} shown)\n`;
      commits.slice(0, 5).forEach((commit, index) => {
        const deltaInfo = getCommitCostDelta(commit, index);
        const deltaDirection = deltaInfo.delta > 0 ? '↑' : deltaInfo.delta < 0 ? '↓' : '→';
        const robustness = deltaInfo.isRobust ? '' : ' (⚠️ incomplete data)';
        report += `Attempt #${commit.attempt}: ${formatCurrency(commit.committed_delta_usd)} ${deltaDirection} ${deltaInfo.basis}${robustness} (${commit.committed_call_ids.length} calls)\n`;
      });
      if (commits.length > 5) {
        report += `... and ${commits.length - 5} more commits\n`;
      }
      report += '\n';
    }

    // Error Details
    if (hasErrors && primaryError) {
      report += `⚠️ Error Details\n`;
      report += `Type: ${primaryError.type}\n`;
      report += `Status: ${primaryError.status}\n`;
      report += `Message: ${primaryError.userMessage}\n`;
      report += `Retryable: ${isRetryable ? 'Yes' : 'No'}\n`;
      if (primaryError.correlationId) {
        report += `Correlation ID: ${primaryError.correlationId}\n`;
      }
      report += 'Remediation Steps:\n';
      primaryError.remediation.forEach((step, index) => {
        report += `  ${index + 1}. ${step}\n`;
      });
      report += '\n';
    }

    // System Status
    report += `💻 System Status\n`;
    report += `Loading: ${loadingState.isLoading ? 'Yes' : 'No'}\n`;
    report += `WebSocket: ${wsConnected ? 'Connected' : 'Disconnected'}\n`;
    report += `Commits Loaded: ${loadingState.commitsFetched}\n`;
    report += `API Calls Loaded: ${loadingState.apiCallsFetched}\n`;
    report += `Page Size: ${pageSize}\n\n`;

    // Metadata
    if (metadata) {
      report += `📋 Metadata\n`;
      report += `Request ID: ${metadata.requestId}\n`;
      report += `Generated At: ${metadata.generatedAt}\n`;
      if (metadata.costDelta !== undefined) {
        report += `Cost Delta: ${formatCurrency(metadata.costDelta)}\n`;
      }
      report += '\n';
    }

    // Footer
    report += '---\n';
    report += '🤖 Generated with Claude Code\n';
    report += `Report ID: ${reportId}\n`;
    report += `Generated: ${timestamp}\n`;
    report += `User Agent: ${navigator.userAgent.slice(0, 100)}...\n`;
    report += `URL: ${window.location.href}\n`;

    return report;
  }, [executionId, commits, pagination, metadata, apiCalls, primaryError, loading, wsConnected, pageSize, getCommitCostDelta]);

  // Copy billing report to clipboard
  const copyBillingReportToClipboard = useCallback(async (): Promise<boolean> => {
    try {
      const report = generateBillingStatusReport();
      await navigator.clipboard.writeText(report);
      addLog('billing', '📋 Billing status report copied to clipboard', 'info', {
        action: 'copy_billing_report',
        reportLength: report.length,
        executionId
      });
      return true;
    } catch (error) {
      addLog('billing', `Failed to copy billing report: ${error}`, 'error', {
        action: 'copy_billing_report_failed',
        error: error instanceof Error ? error.message : String(error)
      });
      return false;
    }
  }, [generateBillingStatusReport, addLog, executionId]);


  // Get API call details for a commit
  const getCommitApiCalls = useCallback((commit: BillingCommit): ApiCall[] => {
    return commit.committed_call_ids
      .map(id => apiCalls[id])
      .filter(Boolean); // Remove undefined entries
  }, [apiCalls]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handle local execution ID changes with debouncing
  const handleExecutionIdChange = useCallback((newId: string) => {
    setLocalExecutionId(newId);

    // Clear existing debounce timer
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the actual execution ID change
    debounceTimeoutRef.current = setTimeout(() => {
      if (onExecutionChange) {
        onExecutionChange(newId);
      }
    }, BILLING_DEBOUNCE_MS);
  }, [onExecutionChange]);

  // Handle manual refresh using React Query invalidation
  const handleRefresh = useCallback(() => {
    addLog('billing', 'Manual refresh triggered', 'info', {
      action: 'manual_refresh',
      executionId
    });

    // Invalidate and refetch both commits and API calls using centralized keys
    demoCacheInvalidation.refetchQuery(queryClient, demoQueryKeys.billingCommits(executionId));
    queryClient.invalidateQueries({
      queryKey: demoQueryKeys.billingApiCallsAggregate(executionId, commitIds)
    });
  }, [addLog, executionId, queryClient]);

  // Sync local execution ID with props
  useEffect(() => {
    if (executionId !== localExecutionId) {
      setLocalExecutionId(executionId);
    }
  }, [executionId, localExecutionId]);

  return (
    <div
      className={`space-y-4 ${className}`}
      data-testid={testId}
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: '8px',
        padding: '16px'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          <h3 className="text-lg font-semibold" style={{ color: 'var(--fg-primary)' }}>
            Billing Commits Inspector
          </h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--accent)' }} />}

          {/* WebSocket Status Indicator */}
          <div className="flex items-center gap-1 px-2 py-1 rounded text-xs" style={{
            backgroundColor: wsConnected ? 'var(--bg-success)' : 'var(--bg-warning)',
            color: wsConnected ? 'var(--fg-success)' : 'var(--fg-warning)'
          }}>
            {wsConnected ? (
              <Wifi className="w-3 h-3" />
            ) : (
              <WifiOff className="w-3 h-3" />
            )}
            <span>{wsConnected ? 'Live' : 'Polling'}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
            Execution ID:
            <input
              type="text"
              value={localExecutionId}
              onChange={(e) => handleExecutionIdChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRefresh();
                }
              }}
              className="px-2 py-1 rounded border font-mono text-xs"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
              }}
              placeholder="exec-123"
              data-testid="billing-execution-input"
            />
          </label>
          <div className="flex items-center gap-1">
            <button
              onClick={copyBillingReportToClipboard}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors"
              style={{
                backgroundColor: 'var(--bg-interactive-hover)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                cursor: 'pointer'
              }}
              title="Copy billing status report to clipboard"
              data-testid="billing-status-report-button"
            >
              <FileText className="w-3 h-3" />
              Report
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors"
              style={{
                backgroundColor: loading ? 'var(--bg-surface)' : 'var(--bg-interactive-hover)',
                borderColor: 'var(--border-default)',
                color: loading ? 'var(--fg-muted)' : 'var(--fg-primary)',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              data-testid="billing-refresh-button"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Permission Denied Notice */}
      {permissionDenied && primaryError && (
        <div
          className="p-3 rounded border"
          style={{
            backgroundColor: 'var(--bg-warning)',
            borderColor: 'var(--border-warning)',
            color: 'var(--fg-warning)'
          }}
          data-testid="billing-permission-denied"
        >
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium">Admin Access Required</div>
              <p className="text-sm mt-1 mb-2">
                {primaryError.userMessage}
              </p>
              {primaryError.remediation.length > 0 && (
                <div className="text-xs">
                  <div className="font-medium mb-1">Try these steps:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {primaryError.remediation.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
              {primaryError.correlationId && (
                <div className="text-xs mt-2 font-mono" style={{ color: 'var(--fg-muted)' }}>
                  ID: {primaryError.correlationId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Other Errors */}
      {error && !permissionDenied && (
        <div
          className="p-3 rounded border"
          style={{
            backgroundColor: primaryError?.retryable ? 'var(--bg-warning)' : 'var(--bg-error)',
            borderColor: primaryError?.retryable ? 'var(--border-warning)' : 'var(--border-error)',
            color: primaryError?.retryable ? 'var(--fg-warning)' : 'var(--fg-error)'
          }}
          data-testid="billing-error"
        >
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">
                {primaryError?.retryable ? 'Temporary Error' : 'Error'}
              </div>
              <p className="text-sm mt-1">
                {primaryError?.userMessage || error}
              </p>
              {primaryError?.remediation && primaryError.remediation.length > 0 && (
                <div className="text-xs mt-2">
                  <div className="font-medium mb-1">Try these steps:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {primaryError.remediation.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
              {primaryError?.correlationId && (
                <div className="text-xs mt-2 font-mono" style={{ color: 'var(--fg-muted)' }}>
                  ID: {primaryError.correlationId}
                </div>
              )}
            </div>
            {primaryError?.retryable && primaryError.canRetryIn && (
              <button
                onClick={handleRefresh}
                className="px-2 py-1 text-xs rounded border transition-colors"
                style={{
                  backgroundColor: 'var(--bg-interactive-hover)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-primary)'
                }}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div
          className="text-center py-8 text-sm"
          style={{ color: 'var(--fg-muted)' }}
        >
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading billing commits...
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && commits.length === 0 && executionId && (
        <div
          className="text-center py-8 text-sm"
          style={{ color: 'var(--fg-muted)' }}
        >
          <Database className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fg-muted)' }} />
          <p className="mb-2">No billing commits found for execution: {executionId}</p>
          <p className="text-xs">This execution may not have any committed API costs yet.</p>
        </div>
      )}

      {/* Commits List */}
      {!loading && commits.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm" style={{ color: 'var(--fg-secondary)' }}>
            <span>Commit History ({pagination?.total || commits.length} commits{pagination && ` - Page ${pagination.page}/${pagination.totalPages}`})</span>
            <span>Pipeline Total: {formatCurrency(metadata?.finalPipelineTotal || commits[0]?.pipeline_total_usd || 0)}</span>
          </div>

          <div className="space-y-1">
            {/* Performance optimization: Use virtualization for large datasets */}
            {(() => {
              const commitCount = commits.length;
              const useVirtualization = shouldVirtualize(commitCount);

              if (useVirtualization) {
                logVirtualizationUsage('commits', commitCount);
                // For now, show a performance notice. Full virtualization can be implemented later.
                return (
                  <div className="p-4 border rounded bg-yellow-500/10 border-yellow-500/30">
                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                      <span className="font-medium">⚡ Performance Mode</span>
                      <span>({commitCount} commits - virtualization recommended)</span>
                    </div>
                    <div className="text-xs text-yellow-300 mt-1">
                      Large dataset detected. Consider implementing pagination or filtering.
                    </div>
                  </div>
                );
              }

              return null; // Fall through to normal rendering
            })()}

            {!shouldVirtualize(commits.length) && commits.map((commit: BillingCommit, index: number) => {
              const isExpanded = expandedCommits.has(commit.id);
              const commitCalls = getCommitApiCalls(commit);

              return (
                <div
                  key={commit.id}
                  className="border rounded"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-subtle)'
                  }}
                >
                  {/* Commit Summary */}
                  <button
                    onClick={() => toggleCommitExpansion(commit.id)}
                    className="w-full p-3 flex items-center justify-between hover:bg-opacity-80 transition-colors"
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--fg-primary)'
                    }}
                    data-testid={`billing-commit-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--fg-muted)' }} />
                      ) : (
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--fg-muted)' }} />
                      )}

                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            Attempt #{commit.attempt}
                          </span>
                          {(() => {
                            const deltaInfo = getCommitCostDelta(commit, index);
                            const visualization = getCostDeltaVisualization(deltaInfo.delta);
                            const Icon = visualization.icon;

                            // Enhanced tooltip with basis information
                            const tooltipText = deltaInfo.isRobust
                              ? `Cost delta: ${visualization.label} (${deltaInfo.basis})`
                              : `Cost delta: ${visualization.label} (${deltaInfo.basis}) - Data may be incomplete`;

                            return (
                              <div className="flex items-center gap-1">
                                <span
                                  className="text-xs px-2 py-0.5 rounded flex items-center gap-1"
                                  style={{
                                    backgroundColor: visualization.bgColor,
                                    color: visualization.color,
                                    opacity: deltaInfo.isRobust ? 1 : 0.8
                                  }}
                                  title={tooltipText}
                                >
                                  <Icon className="w-3 h-3" />
                                  {formatCurrency(commit.committed_delta_usd)} delta
                                  {!deltaInfo.isRobust && <span className="ml-1">⚠️</span>}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyCommitLink(commit);
                                  }}
                                  className="p-1 rounded hover:bg-opacity-20 transition-colors"
                                  style={{ color: 'var(--fg-muted)' }}
                                  title="Copy commit link"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatTimestamp(commit.committed_at)} • {commit.committed_call_ids.length} calls
                        </div>
                      </div>
                    </div>

                    <div className="text-right text-sm">
                      <div style={{ color: 'var(--accent)' }}>
                        {formatCurrency(commit.pipeline_total_usd)}
                      </div>
                      <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                        Pipeline Total
                      </div>
                    </div>
                  </button>

                  {/* Expanded API Calls Details */}
                  {isExpanded && (
                    <div
                      className="border-t p-3"
                      style={{ borderColor: 'var(--border-subtle)' }}
                    >
                      <div className="text-sm font-medium mb-2 flex items-center justify-between" style={{ color: 'var(--fg-primary)' }}>
                        <span>Committed API Calls ({commit.committed_call_ids.length})</span>
                        {commitCalls.length > 0 && (
                          <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                            Total: {formatCurrency(commitCalls.reduce((sum, call) => sum + call.cost_usd, 0))}
                          </div>
                        )}
                      </div>

                      {/* Provider Breakdown */}
                      {commitCalls.length > 0 && (() => {
                        const providerBreakdown = commitCalls.reduce((acc, call) => {
                          const key = `${call.provider}/${call.model}`;
                          if (!acc[key]) {
                            acc[key] = { count: 0, cost: 0, status: {} as Record<string, number> };
                          }
                          acc[key].count += 1;
                          acc[key].cost += call.cost_usd;
                          acc[key].status[call.status] = (acc[key].status[call.status] || 0) + 1;
                          return acc;
                        }, {} as Record<string, { count: number; cost: number; status: Record<string, number> }>);

                        return (
                          <div className="mb-3 p-2 rounded text-xs" style={{ backgroundColor: 'var(--bg-surface)' }}>
                            <div className="font-medium mb-1" style={{ color: 'var(--fg-secondary)' }}>Provider Breakdown:</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {Object.entries(providerBreakdown).map(([provider, stats]) => (
                                <div key={provider} className="flex items-center justify-between">
                                  <span className="font-mono" style={{ color: 'var(--fg-primary)' }}>{provider}</span>
                                  <div className="flex items-center gap-2">
                                    <span style={{ color: 'var(--fg-muted)' }}>{stats.count} calls</span>
                                    <span style={{ color: 'var(--accent)' }}>{formatCurrency(stats.cost)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {commitCalls.length > 0 ? (
                        <div className="space-y-1" data-testid="billing-api-calls-list">
                          {/* Performance optimization: Virtualize API calls if large dataset */}
                          {shouldVirtualize(commitCalls.length) && (
                            <div className="p-2 border rounded bg-blue-500/10 border-blue-500/30 mb-2">
                              <div className="flex items-center gap-2 text-blue-400 text-xs">
                                <span className="font-medium">⚡ Large API Calls List</span>
                                <span>({commitCalls.length} calls - virtualization recommended)</span>
                              </div>
                            </div>
                          )}
                          {commitCalls.slice(0, shouldVirtualize(commitCalls.length) ? 20 : commitCalls.length).map((apiCall, callIndex) => (
                            <div
                              key={apiCall.id}
                              className="flex items-center justify-between p-2 rounded text-xs"
                              style={{
                                backgroundColor: 'var(--bg-surface)',
                                borderLeft: `3px solid ${
                                  apiCall.status === 'completed' ? 'var(--accent-success)' :
                                  apiCall.status === 'failed' ? 'var(--accent-error)' :
                                  'var(--accent-warning)'
                                }`
                              }}
                              data-testid={`billing-api-call-${callIndex}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="flex flex-col">
                                  <span className="font-mono text-xs" style={{ color: 'var(--fg-secondary)' }}>
                                    {apiCall.provider}/{apiCall.model}
                                  </span>
                                  {apiCall.stage_name && (
                                    <span className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                                      Stage: {apiCall.stage_name}
                                    </span>
                                  )}
                                  {apiCall.operation_type && (
                                    <span className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                                      Op: {apiCall.operation_type}
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span
                                    className="px-1.5 py-0.5 rounded text-xs"
                                    style={{
                                      backgroundColor:
                                        apiCall.status === 'completed' ? 'var(--bg-success)' :
                                        apiCall.status === 'failed' ? 'var(--bg-error)' :
                                        'var(--bg-warning)',
                                      color:
                                        apiCall.status === 'completed' ? 'var(--fg-success)' :
                                        apiCall.status === 'failed' ? 'var(--fg-error)' :
                                        'var(--fg-warning)'
                                    }}
                                  >
                                    {apiCall.status}
                                  </span>
                                  {apiCall.retry_count && apiCall.retry_count > 0 && (
                                    <span className="text-xs" style={{ color: 'var(--fg-warning)' }}>
                                      {apiCall.retry_count} retries
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-3 text-right">
                                <div className="flex flex-col items-end text-xs">
                                  {apiCall.input_tokens && apiCall.output_tokens && (
                                    <span style={{ color: 'var(--fg-muted)' }}>
                                      {apiCall.input_tokens}→{apiCall.output_tokens} tokens
                                    </span>
                                  )}
                                  {apiCall.duration_ms && (
                                    <span style={{ color: 'var(--fg-muted)' }}>
                                      {apiCall.duration_ms}ms
                                    </span>
                                  )}
                                  {apiCall.error_message && (
                                    <span
                                      style={{ color: 'var(--fg-error)' }}
                                      title={apiCall.error_message}
                                    >
                                      Error: {apiCall.error_message.slice(0, 20)}...
                                    </span>
                                  )}
                                </div>
                                <span style={{ color: 'var(--accent)' }} className="font-mono">
                                  {formatCurrency(apiCall.cost_usd)}
                                </span>
                              </div>
                            </div>
                          ))}
                          {/* Show truncation notice if virtualization is active */}
                          {shouldVirtualize(commitCalls.length) && commitCalls.length > 20 && (
                            <div className="p-2 text-center text-xs text-gray-400 border-t">
                              Showing first 20 of {commitCalls.length} API calls.
                              <span className="ml-1 text-blue-400">Full virtualization available in future release.</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          className="text-center py-4 text-xs"
                          style={{ color: 'var(--fg-muted)' }}
                          data-testid="billing-no-api-calls"
                        >
                          <Database className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--fg-muted)' }} />
                          <p>No API call details available</p>
                          <p className="mt-1">The commit references {commit.committed_call_ids.length} calls, but details weren't found in the database.</p>
                        </div>
                      )}

                      {/* Missing API calls warning */}
                      {commit.committed_call_ids.length > commitCalls.length && (
                        <div
                          className="mt-2 p-2 rounded text-xs"
                          style={{
                            backgroundColor: 'var(--bg-warning)',
                            color: 'var(--fg-warning)'
                          }}
                          data-testid="billing-missing-api-calls-warning"
                        >
                          ⚠️ {commit.committed_call_ids.length - commitCalls.length} API calls not found in database
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <div className="flex items-center gap-2" style={{ color: 'var(--fg-secondary)' }}>
                <span>Page size:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 rounded border text-xs"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-primary)'
                  }}
                  data-testid="billing-page-size-select"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPreviousPage}
                  className="px-3 py-1 text-xs rounded border transition-colors"
                  style={{
                    backgroundColor: pagination.hasPreviousPage ? 'var(--bg-interactive-hover)' : 'var(--bg-surface)',
                    borderColor: 'var(--border-default)',
                    color: pagination.hasPreviousPage ? 'var(--fg-primary)' : 'var(--fg-muted)',
                    cursor: pagination.hasPreviousPage ? 'pointer' : 'not-allowed'
                  }}
                  data-testid="billing-prev-page"
                >
                  Previous
                </button>

                <span className="px-3 py-1 text-xs" style={{ color: 'var(--fg-secondary)' }}>
                  {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className="px-3 py-1 text-xs rounded border transition-colors"
                  style={{
                    backgroundColor: pagination.hasNextPage ? 'var(--bg-interactive-hover)' : 'var(--bg-surface)',
                    borderColor: 'var(--border-default)',
                    color: pagination.hasNextPage ? 'var(--fg-primary)' : 'var(--fg-muted)',
                    cursor: pagination.hasNextPage ? 'pointer' : 'not-allowed'
                  }}
                  data-testid="billing-next-page"
                >
                  Next
                </button>
              </div>

              <div className="text-xs" style={{ color: 'var(--fg-muted)' }}>
                {pagination.total} total commits
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Stats */}
      {!loading && commits.length > 0 && (
        <div
          className="mt-4 p-3 rounded border text-sm"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            borderColor: 'var(--border-subtle)'
          }}
          data-testid="billing-summary-stats"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div data-testid="billing-stat-commits">
              <div style={{ color: 'var(--accent)' }} className="font-mono">
                {pagination?.total || commits.length}
              </div>
              <div style={{ color: 'var(--fg-muted)' }} className="text-xs">
                {pagination?.total ? 'Total Commits' : 'Page Commits'}
              </div>
            </div>
            <div data-testid="billing-stat-api-calls">
              <div style={{ color: 'var(--accent)' }} className="font-mono">
                {commits.reduce((sum: number, c: BillingCommit) => sum + c.committed_call_ids.length, 0)}
              </div>
              <div style={{ color: 'var(--fg-muted)' }} className="text-xs">
                {pagination?.total ? 'Page API Calls' : 'Total API Calls'}
              </div>
            </div>
            <div data-testid="billing-stat-total-delta">
              <div style={{ color: 'var(--accent)' }} className="font-mono">
                {formatCurrency(metadata?.totalCommittedCost || commits.reduce((sum: number, c: BillingCommit) => sum + c.committed_delta_usd, 0))}
              </div>
              <div style={{ color: 'var(--fg-muted)' }} className="text-xs">
                {pagination?.total ? 'Total Delta (All)' : 'Total Delta (Page)'}
              </div>
            </div>
            <div data-testid="billing-stat-pipeline-total">
              <div style={{ color: 'var(--accent)' }} className="font-mono">
                {formatCurrency(metadata?.finalPipelineTotal || (commits.length > 0 ? commits[0].pipeline_total_usd : 0))}
              </div>
              <div style={{ color: 'var(--fg-muted)' }} className="text-xs">
                Final Pipeline Total
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}